import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PromptDialog from "@/components/PromptDialog";
import {
  Building2, RefreshCw, Search, Filter, ArrowRight, CheckCircle2, XCircle,
  ClipboardList, Users, MessageSquare, Info, Send, RotateCcw, Ban,
  UserCheck, CalendarClock, Loader2, ArrowLeft, Sparkles, ArchiveRestore,
  Copy, Link as LinkIcon, UserPlus, Mail, TrendingUp,
} from "lucide-react";

const STATUS_TONE = {
  submitted: { bg: "#DBEAFE", fg: "#1E40AF" },
  in_review: { bg: "#FEF3C7", fg: "#92400E" },
  info_requested: { bg: "#FEF3C7", fg: "#92400E" },
  approved: { bg: "#DCFCE7", fg: "#166534" },
  rejected: { bg: "#FEE2E2", fg: "#991B1B" },
  archived: { bg: "#F1F5F9", fg: "#475569" },
};

const INV_TONE = {
  pending: { bg: "#F1F5F9", fg: "#475569" },
  sent: { bg: "#DBEAFE", fg: "#1E40AF" },
  delivered: { bg: "#E0F2FE", fg: "#0369A1" },
  activated: { bg: "#DCFCE7", fg: "#166534" },
  expired: { bg: "#FEF3C7", fg: "#92400E" },
  revoked: { bg: "#FEE2E2", fg: "#991B1B" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function Badge({ tone, label, testid }) {
  const t = tone || { bg: "#F1F5F9", fg: "#475569" };
  return (
    <span data-testid={testid}
      style={{ background: t.bg, color: t.fg }}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-widest">
      {label}
    </span>
  );
}

// ---------- LIST PAGE ------------------------------------------------------

function ApplicationsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("applications"); // "applications" | "outreach"
  const [outreach, setOutreach] = useState({ invites: [], count: 0, applied: 0 });
  const [outreachLoading, setOutreachLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (status) q.set("status", status);
      if (search) q.set("search", search);
      const r = await api.get(`/partner-applications?${q.toString()}`);
      setRows(r.data.applications || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status, search]);

  const loadOutreach = useCallback(async () => {
    setOutreachLoading(true);
    try {
      const r = await api.get("/admin/partner-outreach/invites");
      setOutreach(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOutreachLoading(false); }
  }, []);

  const resendProspect = async (pid) => {
    try {
      const { data } = await api.post(`/admin/partner-outreach/invites/${pid}/resend`);
      toast.success(data.email_delivery_ok ? "Reminder sent" : "Resend failed — check delivery logs");
      loadOutreach();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeProspect = async (pid) => {
    if (!window.confirm("Revoke this outreach invitation? The link will stop working.")) return;
    try {
      await api.post(`/admin/partner-outreach/invites/${pid}/revoke`);
      toast.success("Invitation revoked");
      loadOutreach();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "outreach") loadOutreach(); }, [tab, loadOutreach]);

  return (
    <div className="max-w-7xl mx-auto p-8 space-y-5" data-testid="partner-applications-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="hw-eyebrow text-[#F59E0B] mb-1 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Partner Onboarding
          </div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A8A]">Partner Integration Applications</h1>
          <p className="text-sm text-slate-500 mt-1">
            Partner organizations seeking Partner Portal access, APIs, OAuth credentials, webhooks, or
            technical onboarding use the Partner Integration Application at{" "}
            <a
              href="/partner/apply-integrations"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open the Partner Integration Application in a new tab"
              data-testid="link-integration-partner-apply-inline"
              className="font-mono text-[#1E3A8A] hover:underline"
            >
              /partner/apply-integrations
            </a>.
          </p>
        </div>
        <button onClick={tab === "applications" ? load : loadOutreach} data-testid="pa-refresh-btn"
          className="p-2 rounded-full border border-[#E5E5E0] bg-white hover:bg-slate-50">
          <RefreshCw className={`w-4 h-4 ${(loading || outreachLoading) ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Tabs — Iter 76.5/76.7 + IRR-5 tab preparation */}
      <div className="flex items-center gap-1 border-b border-[#E5E5E0]" role="tablist">
        <TabButton
          testid="pa-tab-applications"
          active={tab === "applications"}
          onClick={() => setTab("applications")}
          label="Applications"
          count={rows.length}
        />
        <TabButton
          testid="pa-tab-outreach"
          active={tab === "outreach"}
          onClick={() => setTab("outreach")}
          label="Integration Requests Sent"
          count={outreach.count}
          badge={outreach.applied > 0 ? `${outreach.applied} converted` : null}
        />
        <TabButton
          testid="pa-tab-received"
          disabled
          label="Integration Requests Received"
          badge="Coming Soon"
          helperText="Partner Organizations will be able to submit integration change requests directly from the Partner Portal in a future release."
        />
      </div>

      {tab === "outreach" ? (
        <OutreachList
          invites={outreach.invites}
          loading={outreachLoading}
          onResend={resendProspect}
          onRevoke={revokeProspect}
        />
      ) : (
      <>
      <div className="flex items-center gap-3 bg-white border border-[#E5E5E0] rounded-2xl p-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="pa-search"
            placeholder="Search by name, email, or reference (HW-APP-XXXX)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm focus:outline-none border border-[#E5E5E0]"
          />
        </div>
        <div className="flex items-center gap-1 text-xs">
          <Filter className="w-4 h-4 text-slate-400" />
          <select data-testid="pa-status-filter"
            value={status} onChange={(e) => setStatus(e.target.value)}
            className="px-2 py-2 rounded-lg border border-[#E5E5E0] text-xs">
            <option value="">All statuses</option>
            {Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
        <button onClick={load} data-testid="pa-search-btn"
          className="px-3 py-2 rounded-lg bg-[#1E3A8A] text-white text-xs hover:opacity-90">Apply</button>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400" data-testid="pa-loading">
            <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" /> Loading applications…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="pa-empty">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            No applications match the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-[#F1F5F9]">
              <tr>
                <th className="text-left px-4 py-3">Organization</th>
                <th className="text-left px-4 py-3">Reference</th>
                <th className="text-left px-4 py-3">Primary contact</th>
                <th className="text-left px-4 py-3">Interests</th>
                <th className="text-left px-4 py-3">Reviewers</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`pa-row-${r.id.slice(0, 8)}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.organization?.organization_name}</div>
                    <div className="text-xs text-slate-400">{r.organization?.organization_type?.replace("_", " ")}</div>
                  </td>
                  <td className="px-4 py-3"><code className="text-xs">{r.public_ref}</code></td>
                  <td className="px-4 py-3">
                    <div>{r.primary_contact?.first_name} {r.primary_contact?.last_name}</div>
                    <div className="text-xs text-slate-400">{r.primary_contact?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(r.integration_interests || []).slice(0, 3).map((i) => (
                      <span key={i} className="inline-block bg-[#F0F5FF] text-[#1E40AF] rounded-full px-2 py-0.5 text-[10px] mr-1 mb-1">
                        {i.replace(/_/g, " ")}
                      </span>
                    ))}
                    {(r.integration_interests || []).length > 3 && (
                      <span className="text-slate-400 text-[10px]">+{r.integration_interests.length - 3}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(r.reviewer_emails || []).length === 0 ? (
                      <span className="text-slate-400 italic">unassigned</span>
                    ) : (
                      <span>{r.reviewer_emails.length} reviewer{r.reviewer_emails.length > 1 ? "s" : ""}</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><Badge tone={STATUS_TONE[r.status]} label={r.status.replace("_", " ")} testid={`pa-status-${r.id.slice(0, 8)}`} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmt(r.submitted_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/partner-applications/${r.id}`} data-testid={`pa-open-${r.id.slice(0, 8)}`}
                      className="text-[#1E3A8A] text-xs hover:underline inline-flex items-center gap-1">
                      Review <ArrowRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
}


/* ================================================================= */
/* Iter 76.5 — Outreach: invite prospects to apply                     */
/* (Exported and used by /admin/partners now — see Iter 76.6)          */
/* ================================================================= */

export function TabButton({ testid, active, onClick, label, count, badge, disabled, helperText }) {
  const handleClick = (e) => {
    if (disabled) { e.preventDefault(); return; }
    if (onClick) onClick(e);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testid}
      aria-disabled={disabled ? "true" : undefined}
      aria-selected={active ? "true" : "false"}
      title={disabled ? helperText : undefined}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        disabled
          ? "border-transparent text-slate-400 cursor-not-allowed hover:text-slate-400"
          : active
          ? "border-[#1E3A8A] text-[#1E3A8A]"
          : "border-transparent text-slate-500 hover:text-[#1E3A8A]"
      }`}
    >
      {label}
      {typeof count === "number" && (
        <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
          {count}
        </span>
      )}
      {badge && (
        <span
          className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
            disabled
              ? "bg-slate-100 text-slate-500"
              : "bg-[#DCFCE7] text-[#166534]"
          }`}
        >
          {badge}
        </span>
      )}
      {disabled && helperText && (
        <span className="sr-only"> — {helperText}</span>
      )}
    </button>
  );
}

export function InviteToApplyForm({ onCancel, onSubmit }) {
  const [form, setForm] = useState({
    email: "", org_name: "", contact_name: "", personal_message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const submit = async () => {
    if (!emailValid || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        email: form.email.trim(),
        org_name: form.org_name.trim() || undefined,
        contact_name: form.contact_name.trim() || undefined,
        personal_message: form.personal_message.trim() || undefined,
      });
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="bg-white border-l-4 border-[#1E3A8A] border-t border-r border-b border-[#E5E5E0] rounded-2xl p-6 space-y-4"
      data-testid="invite-to-apply-form"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <UserPlus className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[#0F172A]">Invite a partner to apply</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Sends a branded email with a personal apply link. When the prospect submits their
            application, it will automatically be attributed to your outreach.
          </p>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-medium text-slate-600">
          Prospect Email *
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="ita-email"
            placeholder="director@example.org"
            className="mt-1 w-full px-3 py-2 rounded-lg text-sm border border-[#E5E5E0] focus:outline-none focus:border-[#1E3A8A]"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Organization Name
          <input
            value={form.org_name}
            onChange={(e) => setForm({ ...form, org_name: e.target.value })}
            data-testid="ita-org"
            placeholder="Optional"
            className="mt-1 w-full px-3 py-2 rounded-lg text-sm border border-[#E5E5E0] focus:outline-none focus:border-[#1E3A8A]"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Contact Name
          <input
            value={form.contact_name}
            onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
            data-testid="ita-contact"
            placeholder="Optional"
            className="mt-1 w-full px-3 py-2 rounded-lg text-sm border border-[#E5E5E0] focus:outline-none focus:border-[#1E3A8A]"
          />
        </label>
        <label className="text-xs font-medium text-slate-600 sm:col-span-2">
          Personal Message
          <textarea
            value={form.personal_message}
            onChange={(e) => setForm({ ...form, personal_message: e.target.value })}
            data-testid="ita-message"
            rows={3}
            maxLength={600}
            placeholder="Optional — appears as a highlighted quote in the invitation email."
            className="mt-1 w-full px-3 py-2 rounded-lg text-sm border border-[#E5E5E0] focus:outline-none focus:border-[#1E3A8A]"
          />
          <div className="text-[10px] text-slate-400 mt-1 text-right">
            {form.personal_message.length}/600
          </div>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-full"
          data-testid="ita-cancel"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!emailValid || submitting}
          data-testid="ita-submit"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <Send className="w-3.5 h-3.5" /> Send invitation
        </button>
      </div>
    </div>
  );
}


const OUTREACH_STATUS_TONE = {
  sent: { bg: "#DBEAFE", fg: "#1E40AF", label: "Sent" },
  opened: { bg: "#FEF3C7", fg: "#92400E", label: "Opened" },
  applied: { bg: "#DCFCE7", fg: "#166534", label: "Applied" },
  failed: { bg: "#FEE2E2", fg: "#991B1B", label: "Email failed" },
  revoked: { bg: "#F1F5F9", fg: "#475569", label: "Revoked" },
};

export function OutreachList({ invites, loading, freshUrls = {}, onResend, onRevoke }) {
  if (loading && invites.length === 0) {
    return (
      <div className="p-10 text-center text-slate-400" data-testid="outreach-loading">
        <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" /> Loading outreach…
      </div>
    );
  }
  if (invites.length === 0) {
    return (
      <div className="p-10 text-center text-slate-500 bg-white border border-[#E5E5E0] rounded-2xl"
             data-testid="outreach-empty">
        <Mail className="w-10 h-10 mx-auto mb-2 text-slate-300" />
        <div className="font-medium">No outreach yet</div>
        <div className="text-xs mt-1">Click &ldquo;Invite a partner to apply&rdquo; to send your first prospect email.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="outreach-list">
      {invites.map((inv) => (
        <OutreachRow
          key={inv.id}
          invite={inv}
          freshUrl={freshUrls[inv.id]}
          onResend={onResend}
          onRevoke={onRevoke}
        />
      ))}
    </div>
  );
}

function OutreachRow({ invite, freshUrl, onResend, onRevoke }) {
  const tone = OUTREACH_STATUS_TONE[invite.status] || OUTREACH_STATUS_TONE.sent;
  const isTerminal = invite.status === "applied" || invite.status === "revoked";
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(freshUrl || invite.apply_url);
      toast.success("Apply link copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4"
           data-testid={`outreach-row-${invite.id.slice(0, 8)}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[#0F172A]">
              {invite.org_name || invite.email}
            </div>
            <span
              style={{ background: tone.bg, color: tone.fg }}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest"
              data-testid={`outreach-status-${invite.id.slice(0, 8)}`}
            >
              {tone.label}
            </span>
            {invite.resent_count > 0 && (
              <span className="text-[10px] text-slate-400">
                resent {invite.resent_count}×
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {invite.contact_name ? `${invite.contact_name} · ` : ""}{invite.email}
          </div>
          <div className="text-[11px] text-slate-400 mt-1 flex gap-4 flex-wrap">
            <span>Invited {new Date(invite.sent_at).toLocaleString()}</span>
            {invite.opened_at && <span>Opened {new Date(invite.opened_at).toLocaleString()}</span>}
            {invite.applied_at && (
              <span className="text-[#166534] font-medium">
                Applied {new Date(invite.applied_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {invite.application_id && (
            <Link
              to={`/admin/partner-applications/${invite.application_id}`}
              data-testid={`outreach-open-app-${invite.id.slice(0, 8)}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#DCFCE7] text-[#166534] text-xs font-medium hover:opacity-90"
            >
              <TrendingUp className="w-3 h-3" /> Review application
            </Link>
          )}
          {!isTerminal && (
            <>
              <button
                onClick={copyUrl}
                data-testid={`outreach-copy-${invite.id.slice(0, 8)}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E0] text-xs hover:bg-slate-50"
                title="Copy the apply link"
              >
                <Copy className="w-3 h-3" /> Copy link
              </button>
              <button
                onClick={() => onResend(invite.id)}
                data-testid={`outreach-resend-${invite.id.slice(0, 8)}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs hover:opacity-90"
              >
                <Send className="w-3 h-3" /> Resend
              </button>
              <button
                onClick={() => onRevoke(invite.id)}
                data-testid={`outreach-revoke-${invite.id.slice(0, 8)}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#FEE2E2] text-[#991B1B] text-xs hover:bg-[#FEF2F2]"
              >
                <Ban className="w-3 h-3" /> Revoke
              </button>
            </>
          )}
        </div>
      </div>
      {freshUrl && (
        <div
          className="mt-3 border border-[#FCD34D] bg-[#FFFBEB] rounded-xl p-3 space-y-2"
          data-testid={`outreach-fresh-link-${invite.id.slice(0, 8)}`}
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#92400E]">
            <LinkIcon className="w-3 h-3" /> Apply link
          </div>
          <div className="flex items-stretch gap-2">
            <input
              readOnly value={freshUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 text-xs px-2 py-1.5 rounded-lg border border-[#FCD34D] bg-white font-mono text-[#7C2D12]"
            />
            <button
              onClick={copyUrl}
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F59E0B] text-white hover:opacity-90"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- DETAIL PAGE ----------------------------------------------------

function ProgressTracker({ progress }) {
  if (!progress?.stages) return null;
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="pa-progress-tracker">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#7E937D]">Onboarding Progress</div>
          <h3 className="font-semibold text-[#1E3A8A]">{progress.definition_name}</h3>
        </div>
        <div className="text-xs text-slate-400">
          {progress.completed_at ? `Completed ${fmt(progress.completed_at)}` :
            progress.current_stage ? `At: ${progress.current_stage.replace(/_/g, " ")}` : "Not started"}
        </div>
      </div>
      <ol className="space-y-2">
        {progress.stages.map((s) => {
          const dotBg = s.status === "completed" ? "#166534"
            : s.status === "current" ? "#F59E0B" : "#CBD5E1";
          return (
            <li key={s.key}
                data-testid={`pa-stage-${s.key}`}
                className={`flex items-start gap-3 p-2 rounded-lg ${
                  s.status === "current" ? "bg-[#FFFBEB]" : ""
                }`}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: dotBg, color: "white" }}>
                {s.status === "completed" ? <CheckCircle2 className="w-4 h-4" /> : <span className="text-xs">{s.order}</span>}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{s.label}</div>
                <div className="text-xs text-slate-500">{s.description}</div>
                {s.entered_at && (
                  <div className="text-[10px] text-slate-400 mt-0.5">{fmt(s.entered_at)}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function InvitationCard({ invitation, onChanged, initialFreshUrl = null }) {
  const [busy, setBusy] = useState(null);
  const [reassignEmail, setReassignEmail] = useState("");
  const [reassignName, setReassignName] = useState("");
  const [showReassign, setShowReassign] = useState(false);
  const [prompt, setPrompt] = useState(null);
  // Reveal the one-time activation URL the server returned on the last
  // resend/regenerate/reassign. This is the ONLY moment the raw token is
  // available (we never persist it), so admins can copy/paste to hand off
  // manually if email delivery fails.
  const [freshUrl, setFreshUrl] = useState(initialFreshUrl);
  useEffect(() => { if (initialFreshUrl) setFreshUrl(initialFreshUrl); }, [initialFreshUrl]);
  const act = async (action, path, body = {}) => {
    setBusy(action);
    try {
      const { data } = await api.post(path, body);
      toast.success(`${action} complete`);
      if (data?.activation_url) setFreshUrl(data.activation_url);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const copyLink = async () => {
    if (!freshUrl) return;
    try {
      await navigator.clipboard.writeText(freshUrl);
      toast.success("Activation link copied");
    } catch {
      toast.error("Copy failed — select the link and copy manually.");
    }
  };
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid={`pa-inv-${invitation.id.slice(0, 8)}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Portal Owner Invitation</div>
          <div className="font-semibold">{invitation.portal_owner_name || invitation.portal_owner_email}</div>
          <div className="text-xs text-slate-500">{invitation.portal_owner_email}</div>
        </div>
        <Badge tone={INV_TONE[invitation.status]} label={invitation.status}
                testid={`pa-inv-status-${invitation.id.slice(0, 8)}`} />
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs mb-4">
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px]">Expires</div>
          <div className="font-medium">{fmt(invitation.token_expires_at)}</div>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px]">Resent count</div>
          <div className="font-medium">{invitation.resent_count || 0}</div>
        </div>
        {invitation.activated_at && (
          <div className="col-span-2">
            <div className="text-slate-400 uppercase tracking-wider text-[10px]">Activated</div>
            <div className="font-medium text-[#166534]">{fmt(invitation.activated_at)}</div>
          </div>
        )}
      </div>
      {freshUrl && (
        <div
          className="mb-4 border border-[#F59E0B]/40 bg-[#FFFBEB] rounded-xl p-3 space-y-2"
          data-testid={`pa-inv-fresh-link-${invitation.id.slice(0, 8)}`}
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#92400E]">
            <LinkIcon className="w-3 h-3" /> One-time activation link
          </div>
          <div className="text-[11px] text-[#92400E] leading-relaxed">
            Copy this link now — it will not be shown again after you leave this page.
            Share it out-of-band (secure channel) with the Portal Owner if email did not arrive.
          </div>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={freshUrl}
              onFocus={(e) => e.target.select()}
              data-testid={`pa-inv-fresh-link-input-${invitation.id.slice(0, 8)}`}
              className="flex-1 min-w-0 text-xs px-2 py-1.5 rounded-lg border border-[#FCD34D] bg-white font-mono text-[#7C2D12]"
            />
            <button
              onClick={copyLink}
              data-testid={`pa-inv-copy-link-${invitation.id.slice(0, 8)}`}
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F59E0B] text-white hover:opacity-90"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>
      )}
      {invitation.status !== "activated" && invitation.status !== "revoked" && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => act("Resend", `/partner-invitations/${invitation.id}/resend`, { reason: "operator initiated" })}
                  disabled={!!busy}
                  data-testid={`pa-inv-resend-${invitation.id.slice(0, 8)}`}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white hover:opacity-90">
            {busy === "Resend" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend
          </button>
          <button onClick={() => act("Regenerate", `/partner-invitations/${invitation.id}/regenerate`)}
                  disabled={!!busy}
                  data-testid={`pa-inv-regen-${invitation.id.slice(0, 8)}`}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E0] hover:bg-slate-50">
            {busy === "Regenerate" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Regenerate
          </button>
          <button onClick={() => act("Extend", `/partner-invitations/${invitation.id}/extend`, { additional_days: 7 })}
                  disabled={!!busy}
                  data-testid={`pa-inv-extend-${invitation.id.slice(0, 8)}`}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E0] hover:bg-slate-50">
            {busy === "Extend" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarClock className="w-3 h-3" />} +7 days
          </button>
          <button onClick={() => setShowReassign(!showReassign)}
                  disabled={!!busy}
                  data-testid={`pa-inv-reassign-toggle-${invitation.id.slice(0, 8)}`}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E0] hover:bg-slate-50">
            <UserCheck className="w-3 h-3" /> Reassign
          </button>
          <button onClick={() => setPrompt({
                    title: "Revoke invitation",
                    description: "This will invalidate the current invitation token.",
                    mode: "text",
                    placeholder: "Reason (required)",
                    required: true,
                    tone: "danger",
                    confirmLabel: "Revoke",
                    onConfirm: (reason) => act("Revoke", `/partner-invitations/${invitation.id}/revoke`, { reason }),
                  })}
                  disabled={!!busy}
                  data-testid={`pa-inv-revoke-${invitation.id.slice(0, 8)}`}
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEF2F2]">
            <Ban className="w-3 h-3" /> Revoke
          </button>
        </div>
      )}
      {showReassign && (
        <div className="mt-3 border border-[#F1F5F9] rounded-xl p-3 space-y-2">
          <input placeholder="New Portal Owner email" value={reassignEmail}
            data-testid={`pa-inv-reassign-email-${invitation.id.slice(0, 8)}`}
            onChange={(e) => setReassignEmail(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm" />
          <input placeholder="Full name (optional)" value={reassignName}
            data-testid={`pa-inv-reassign-name-${invitation.id.slice(0, 8)}`}
            onChange={(e) => setReassignName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm" />
          <button onClick={() => {
                    if (!reassignEmail.trim()) { toast.error("New email is required"); return; }
                    act("Reassign", `/partner-invitations/${invitation.id}/reassign`, { new_email: reassignEmail, new_name: reassignName || undefined });
                    setShowReassign(false); setReassignEmail(""); setReassignName("");
                  }}
                  data-testid={`pa-inv-reassign-submit-${invitation.id.slice(0, 8)}`}
                  className="text-xs bg-[#1E3A8A] text-white px-3 py-1.5 rounded-full">
            Reassign to new Portal Owner
          </button>
        </div>
      )}
      <PromptDialog prompt={prompt} onClose={() => setPrompt(null)} />
    </div>
  );
}

function ApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [progress, setProgress] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [reviewers, setReviewers] = useState("");
  const [prompt, setPrompt] = useState(null);
  // Map of invitation.id → one-time activation URL (only populated when the
  // server returns it on approve/resend/regenerate/reassign — the raw token
  // is never persisted, so this is our single chance to surface it).
  const [freshUrls, setFreshUrls] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [a, p, t] = await Promise.all([
        api.get(`/partner-applications/${id}`),
        api.get(`/partner-applications/${id}/progress`),
        api.get(`/partner-applications/${id}/timeline`),
      ]);
      setApp(a.data.application);
      setInvitations(a.data.invitations || []);
      setProgress(p.data);
      setTimeline(t.data.events || []);
      setReviewers((a.data.application.reviewer_emails || []).join(", "));
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading && !app) {
    return <div className="p-10 text-center text-slate-500" data-testid="pa-detail-loading">
      <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" /> Loading application…
    </div>;
  }
  if (error && !app) {
    return <div className="p-10 text-center text-[#991B1B]" data-testid="pa-detail-error">
      <XCircle className="w-6 h-6 inline-block mr-2" /> {error}
    </div>;
  }
  if (!app) return null;

  const submit = async (path, body, label) => {
    try {
      const { data } = await api.post(`/partner-applications/${app.id}/${path}`, body);
      toast.success(`${label} successful`);
      // Preserve the one-time activation URL from the approve response so
      // the admin can copy it in case the outbound email doesn't arrive.
      if (data?.invitation?.id && data?.invitation?.activation_url) {
        setFreshUrls((prev) => ({
          ...prev,
          [data.invitation.id]: data.invitation.activation_url,
        }));
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = () => {
    setPrompt({
      title: "Approve application?",
      description: `This will auto-create the Partner Organization, mint the first Portal Owner invitation for ${app.primary_contact.email}, and send the activation email.`,
      mode: "confirm",
      confirmLabel: "Approve & provision",
      tone: "primary",
      onConfirm: () => submit("approve", {}, "Approval"),
    });
  };
  const reject = () => {
    setPrompt({
      title: "Reject application",
      description: "The applicant will receive a branded email explaining the decision.",
      mode: "textarea",
      placeholder: "Reason (required — this will be sent to the applicant)",
      required: true,
      tone: "danger",
      confirmLabel: "Reject & notify",
      onConfirm: (reason) => submit("reject", { reason, notify_applicant: true }, "Rejection"),
    });
  };
  const archive = () => {
    setPrompt({
      title: "Archive application",
      description: "Removes this application from active views. Not visible to applicants.",
      mode: "text",
      placeholder: "Reason (optional)",
      required: false,
      tone: "primary",
      confirmLabel: "Archive",
      onConfirm: (reason) => submit("archive", { reason: reason || null }, "Archive"),
    });
  };
  const requestInfo = async () => {
    if (!infoMsg.trim()) { toast.error("Enter a message"); return; }
    await submit("request-info", { message: infoMsg, send_email: true }, "Info request");
    setInfoMsg("");
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await submit("notes", { note, internal_only: true }, "Note added");
    setNote("");
  };
  const saveReviewers = async () => {
    const list = reviewers.split(",").map((s) => s.trim()).filter(Boolean);
    await submit("reviewers", { reviewer_emails: list }, "Reviewers updated");
  };

  return (
    <div className="max-w-7xl mx-auto p-8 space-y-5" data-testid="pa-detail-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav("/admin/partner-applications")}
                data-testid="pa-back-btn"
                className="p-2 rounded-full border border-[#E5E5E0] hover:bg-slate-50">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-semibold text-[#1E3A8A]">
              {app.organization.organization_name}
            </h1>
            <Badge tone={STATUS_TONE[app.status]} label={app.status.replace("_", " ")} testid="pa-detail-status" />
          </div>
          <div className="text-xs text-slate-400 mt-1">
            <code>{app.public_ref}</code> · Submitted {fmt(app.submitted_at)}
          </div>
        </div>
        {app.status === "submitted" || app.status === "in_review" || app.status === "info_requested" ? (
          <div className="flex items-center gap-2">
            <button onClick={approve} data-testid="pa-approve-btn"
                    className="inline-flex items-center gap-1 bg-[#166534] text-white text-sm px-4 py-2 rounded-full hover:opacity-90">
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
            <button onClick={reject} data-testid="pa-reject-btn"
                    className="inline-flex items-center gap-1 border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-2 rounded-full hover:bg-[#FEF2F2]">
              <XCircle className="w-4 h-4" /> Reject
            </button>
          </div>
        ) : null}
        <button onClick={archive} data-testid="pa-archive-btn"
                className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1">
          <ArchiveRestore className="w-3 h-3" /> Archive
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Basic info */}
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="pa-detail-summary">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-[#1E3A8A]" />
              <h2 className="font-semibold text-[#1E3A8A]">Application details</h2>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Info2 label="Organization type" value={app.organization.organization_type?.replace("_", " ")} />
              <Info2 label="Website" value={app.organization.website || "—"} />
              <Info2 label="Service area" value={app.organization.primary_service_area} />
              <Info2 label="Physical address" value={app.organization.physical_address} />
              <Info2 label="Primary contact" value={`${app.primary_contact.first_name} ${app.primary_contact.last_name}`} />
              <Info2 label="Primary email" value={app.primary_contact.email} />
              <Info2 label="Executive" value={`${app.executive_contact.name} · ${app.executive_contact.email}`} />
              <Info2 label="Interests" value={(app.integration_interests || []).join(", ") || "—"} />
              <Info2 label="Mission" value={app.details?.mission_statement} full />
              <Info2 label="Programs & services" value={app.details?.programs_and_services} full />
              <Info2 label="Populations served" value={app.details?.populations_served} />
              <Info2 label="Counties / regions" value={app.details?.counties_regions_served} />
              {app.other_notes && <Info2 label="Additional notes" value={app.other_notes} full />}
            </div>
          </div>

          {/* Reviewers */}
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#1E3A8A]" />
              <h2 className="font-semibold text-[#1E3A8A]">Reviewers</h2>
            </div>
            <div className="flex gap-2">
              <input value={reviewers} onChange={(e) => setReviewers(e.target.value)}
                data-testid="pa-reviewers-input"
                placeholder="Comma-separated emails, e.g. eric@harborwell.care, ops@harborwell.care"
                className="flex-1 px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm" />
              <button onClick={saveReviewers} data-testid="pa-reviewers-save"
                className="text-xs bg-[#1E3A8A] text-white px-4 py-2 rounded-full">Save</button>
            </div>
          </div>

          {/* Request more info */}
          {(app.status === "submitted" || app.status === "in_review") && (
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-4 h-4 text-[#1E3A8A]" />
                <h2 className="font-semibold text-[#1E3A8A]">Request additional information</h2>
              </div>
              <textarea value={infoMsg} onChange={(e) => setInfoMsg(e.target.value)}
                data-testid="pa-info-msg"
                placeholder="What do you need from the applicant?"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm min-h-[80px] mb-2" />
              <button onClick={requestInfo} data-testid="pa-info-send"
                className="text-xs bg-[#1E3A8A] text-white px-4 py-2 rounded-full inline-flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Send to applicant
              </button>
            </div>
          )}

          {/* Notes */}
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="pa-notes-card">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-[#1E3A8A]" />
              <h2 className="font-semibold text-[#1E3A8A]">Reviewer notes</h2>
            </div>
            {(app.review_notes || []).length === 0 && (
              <div className="text-xs text-slate-400 mb-3" data-testid="pa-notes-empty">No notes yet.</div>
            )}
            <ul className="space-y-2 mb-4">
              {(app.review_notes || []).map((n) => (
                <li key={n.id} className="border border-[#F1F5F9] rounded-xl p-3">
                  <div className="text-sm">{n.note}</div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    {n.author_email} · {fmt(n.at)}
                  </div>
                </li>
              ))}
            </ul>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="pa-note-input"
              placeholder="Add an internal note…"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm min-h-[60px] mb-2" />
            <button onClick={addNote} data-testid="pa-note-add-btn"
              className="text-xs bg-[#1E3A8A] text-white px-4 py-2 rounded-full">Add note</button>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="pa-timeline-card">
            <h2 className="font-semibold text-[#1E3A8A] mb-3">Audit timeline</h2>
            <ol className="space-y-2 max-h-96 overflow-auto">
              {timeline.map((e) => (
                <li key={e.id} className="text-xs border-l-2 border-[#E5E5E0] pl-3">
                  <div className="font-medium">{e.action}</div>
                  <div className="text-slate-500">{fmt(e.at)} · {e.actor?.email || "system"}</div>
                  {e.details && Object.keys(e.details).length > 0 && (
                    <div className="text-slate-400 text-[10px] mt-0.5">
                      {Object.entries(e.details).slice(0, 3)
                        .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
                        .join(" · ")}
                    </div>
                  )}
                </li>
              ))}
              {timeline.length === 0 && (
                <li className="text-xs text-slate-400" data-testid="pa-timeline-empty">No audit events yet.</li>
              )}
            </ol>
          </div>
        </div>

        <div className="space-y-5">
          <ProgressTracker progress={progress} />
          {invitations.length > 0 && (
            <div className="space-y-3">
              {invitations.map((inv) => (
                <InvitationCard
                  key={inv.id}
                  invitation={inv}
                  initialFreshUrl={freshUrls[inv.id]}
                  onChanged={load}
                />
              ))}
            </div>
          )}
          {app.partner_org_id && (
            <div className="bg-[#F0F5FF] border border-[#DBEAFE] rounded-2xl p-4 text-sm" data-testid="pa-partner-org-link">
              <div className="text-[10px] uppercase tracking-widest text-[#1E40AF] mb-1">Provisioned Partner Organization</div>
              <div className="font-medium">{app.organization.organization_name}</div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">{app.partner_slug}</div>
              <Link to="/admin/partners"
                className="text-xs text-[#1E3A8A] hover:underline inline-flex items-center gap-1 mt-2">
                Open partner org <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      </div>
      <PromptDialog prompt={prompt} onClose={() => setPrompt(null)} />
    </div>
  );
}

function Info2({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

// ---------- ROUTER SHELL ---------------------------------------------------

export default function PartnerApplicationsAdmin() {
  const { id } = useParams();
  return id ? <ApplicationDetail /> : <ApplicationsList />;
}
